import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  Disclose,
  EmptyState,
  Figure,
  Figures,
  Headline,
  Legend,
  Loading,
  Proportion,
  Refusal,
  StackedBars,
  Tooltip,
  readClock,
} from "@lopie/kernel-ui";

import AgentControls from "../components/AgentControls.jsx";
import Kept from "../components/Kept.jsx";

/**
 * What have the agents actually done?
 *
 * ## Why this is not the console's Activity screen
 *
 * That one is the whole audit log — connectors, apps and agents together —
 * and on it `xero` wrote 2,308 records while the two agents wrote one between
 * them. Every chart on that page is a chart of a connector sync with two
 * agents rounded off the bottom, which is the correct picture of *this
 * kernel* and useless as a picture of *its agents*.
 *
 * So this asks a narrower question and reads differently for it. The rows are
 * fetched **per agent**, `GET /audit?agent=<name>`, so the chart is of each
 * agent's own history rather than of whatever a 200-row window happened to
 * contain. Two agents is two requests; the cap below is why that does not
 * become twenty.
 *
 * ## Two things called activity, and they are not the same
 *
 * **Runs** are an agent waking up — the schedule story, from `/agents`.
 * **Writes** are what it changed — the effect story, from the audit log.
 *
 * An agent can run every hour and write nothing, which is a perfectly healthy
 * read-only agent, and it can write once and never run again, which is an
 * agent somebody triggered by hand. A screen that showed one number would be
 * answering a different question from the one it was asked, so both are here
 * and they are labelled differently.
 *
 * ## The schedule is on this screen rather than behind a settings page
 *
 * *When did this run* and *when should it run* are the same conversation. A
 * person looking at a gap in the chart wants to change the cadence without
 * going somewhere else to do it, and the window control is right under the
 * history it explains.
 */

/**
 * How many agents to fetch writes for.
 *
 * One request each. Two is nothing and twenty is a screen that takes four
 * seconds to say very little — so past this the chart is drawn from the ones
 * it has and the page says which, rather than quietly showing a subset as if
 * it were everything.
 */
const MAX_AGENTS = 12;

/** Rows per agent. Larger than the chart's window because it is one agent. */
const ROWS = 200;

function relative(iso) {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const count = (n) => Number(n || 0).toLocaleString("en-US");

function dollars(usd) {
  if (!usd) return "$0.00";
  return usd < 0.01 ? "<$0.01" : `$${usd.toFixed(2)}`;
}

/** `contact-of-record` gives `COR`; `xero` gives `XE`. */
function initials(name) {
  const parts = String(name).split(/[-_ ]/).filter(Boolean);
  if (parts.length > 1) {
    return parts.slice(0, 3).map((p) => p[0]).join("").toUpperCase();
  }
  return String(name).slice(0, 2).toUpperCase();
}

export default function Activity({ get, post, onEffective }) {
  const [roster, setRoster] = useState(null);
  const [writes, setWrites] = useState(null);
  const [failure, setFailure] = useState(null);
  const [span, setSpan] = useState("");
  const [only, setOnly] = useState("");
  const [readAt, setReadAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(true);
  // The *name* of the agent whose controls are open, not the row itself.
  //
  // Holding the row was the obvious thing and it was wrong: every control in
  // the drawer saves and then reloads the roster, and a held object does not
  // change when the list behind it does. So switching an agent off returned
  // 200, the card behind the drawer updated, and the drawer went on saying
  // "On" — which reads as the save having failed. Deriving it from the
  // freshly-loaded roster on every render is one line and cannot drift.
  const [openName, setOpenName] = useState(null);

  const theme = useTheme();
  const palette = theme.palette.series || [theme.palette.primary.main];

  const since = useMemo(() => {
    if (!span) return "";
    const days = { "24h": 1, "7d": 7, "30d": 30 }[span];
    if (!days) return "";
    return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  }, [span]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await get("/agents");
      const agents = (body.agents || []).slice(0, MAX_AGENTS);
      setRoster({ ...body, agents, truncated: (body.agents || []).length > MAX_AGENTS });

      // One read per agent. `agent` is one of the six attribute filters the
      // endpoint accepts, so the kernel walks every action partition and
      // matches after the read — which is why this is per agent and not one
      // unfiltered page: an agent's whole history is the question here.
      const pages = await Promise.all(
        agents.map((a) =>
          get("/audit", { agent: a.name, limit: ROWS, sort: "desc", since })
            .then((page) => ({ agent: a, page }))
            .catch((e) => ({ agent: a, error: e })),
        ),
      );
      setWrites(pages);
      setReadAt(Date.now());
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setRoster(null);
    } finally {
      setLoading(false);
    }
  }, [get, since]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  const open = openName
    ? (roster?.agents || []).find((a) => a.name === openName) || null
    : null;

  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    const tick = setInterval(() => {
      setNow(Date.now());
      if (!live || document.hidden) return;
      if (Date.now() - (readAt || 0) >= 30000) latest.current();
    }, 5000);
    return () => clearInterval(tick);
  }, [live, readAt]);

  /**
   * The shape, bucketed by day and stacked by agent.
   *
   * Each agent's rows come from its own read, so a bucket is a true count of
   * that agent's writes on that day rather than a sample. The span is still
   * the union of what came back, which the caption says.
   */
  const shape = useMemo(() => {
    if (!writes) return null;

    const rows = [];
    for (const { agent, page } of writes) {
      for (const row of page?.items || []) rows.push({ agent: agent.name, row });
    }
    if (!rows.length) return { rows: [], buckets: [], series: [], byAgent: {} };

    const times = rows
      .map(({ row }) => Date.parse(row.at))
      .filter((n) => !Number.isNaN(n));
    const first = Math.min(...times);
    const last = Math.max(...times);
    const hourly = last - first < 48 * 3600 * 1000;
    const size = hourly ? 3600000 : 86400000;
    const key = (ms) => {
      const d = new Date(Math.floor(ms / size) * size);
      return hourly
        ? `${d.toISOString().slice(0, 13).replace("T", " ")}:00`
        : d.toISOString().slice(0, 10);
    };

    const cells = new Map();
    const byAgent = {};
    for (const { agent, row } of rows) {
      const ms = Date.parse(row.at);
      if (Number.isNaN(ms)) continue;
      const bucket = key(ms);
      if (!cells.has(bucket)) cells.set(bucket, {});
      cells.get(bucket)[agent] = (cells.get(bucket)[agent] || 0) + 1;
      byAgent[agent] = (byAgent[agent] || 0) + 1;
    }

    const buckets = [];
    for (let ms = Math.floor(first / size) * size; ms <= last; ms += size) {
      const label = key(ms);
      buckets.push({ label, values: cells.get(label) || {} });
      if (buckets.length > 90) break;
    }

    const series = Object.keys(byAgent).sort((a, b) => byAgent[b] - byAgent[a]);
    return { rows, buckets, series, byAgent, unit: hourly ? "hourly" : "daily" };
  }, [writes]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!roster || !shape) return <Loading label="Reading what the agents did" />;

  const agents = roster.agents || [];
  if (!agents.length) {
    return (
      <EmptyState title="No app in this kernel declares an agent">
        <Typography variant="body2">
          There is no activity to show because there is nobody to have any. An
          app declares an agent in a committed manifest or in a request
          approved through LO-61, and nowhere else.
        </Typography>
      </EmptyState>
    );
  }

  const shown = only ? agents.filter((a) => a.name === only) : agents;
  const totalWrites = only ? shape.byAgent[only] || 0 : shape.rows.length;
  const totalRuns = shown.reduce((n, a) => n + (a.spend?.runs || 0), 0);
  const spent = shown.reduce((n, a) => n + (a.spend?.usd || 0), 0);
  const stopped = writes.filter(({ page }) => page?.stopped_early).length;

  return (
    <Stack spacing={3}>
      <Headline
        eyebrow={only ? `${only}, on this page` : "By every declared agent"}
        figure={count(totalWrites)}
        caption={`write${totalWrites === 1 ? "" : "s"}${
          since ? " in this window" : " on record"
        }`}
        basis="Read per agent rather than out of one page of the log, so this is each agent's own history and not a sample of everything. The endpoint bounds its walk and says when it stopped early; the page says so too when it does."
        right={
          <Stack spacing={1.25} sx={{ alignItems: { xs: "flex-start", sm: "flex-end" } }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={span}
              onChange={(_e, next) => next && setSpan(next === "All" ? "" : next)}
              aria-label="How far back to read"
            >
              {["24h", "7d", "30d", "All"].map((name) => (
                <ToggleButton key={name} value={name}>
                  {name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Freshness
              readAt={readAt}
              now={now}
              live={live}
              loading={loading}
              onToggle={() => setLive((was) => !was)}
              onRefresh={load}
            />
          </Stack>
        }
      >
        <Figures sx={{ mt: 2.5 }}>
          <Figure
            value={count(totalRuns)}
            label="runs · recorded"
            basis="An agent waking up, which is a different fact from a write. An agent can run every hour and write nothing — that is a healthy read-only agent — and it can write once and never run again, which is one somebody triggered by hand."
          />
          <Figure
            value={dollars(spent)}
            label="spent · at list"
            basis="Computed from each session's reported usage at list prices, not read from a bill."
          />
          <Figure
            value={count(shown.length)}
            label={`agent${shown.length === 1 ? "" : "s"}`}
            basis="Declared in a committed manifest somebody reviewed."
          />
        </Figures>
      </Headline>

      {roster.truncated ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Showing the first {MAX_AGENTS} agents</AlertTitle>
          This page reads one page of history per agent, so it caps how many it
          will ask for. The chart and the cards below are of those agents and
          not of every one this kernel declares.
        </Alert>
      ) : null}

      {stopped ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ borderColor: "kernel.attention" }}
        >
          <AlertTitle>
            {stopped} of these reads stopped early
          </AlertTitle>
          The kernel bounds how far it walks looking for one agent&apos;s rows.
          Those agents have more history than is drawn here, so a short bar is
          the read running out rather than a quiet day.
        </Alert>
      ) : null}

      {shape.buckets.length > 1 ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h2">Over time, by agent</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stacked by agent · {shape.unit} buckets · each agent&apos;s own
            history, read separately
          </Typography>
          <StackedBars
            buckets={shape.buckets}
            series={only ? [only] : shape.series}
            colours={palette}
            caption={`${shape.unit} buckets`}
            label={`Writes per ${shape.unit} across ${shape.buckets.length} buckets, stacked by each of ${shape.series.length} agents.`}
          />
          <Legend
            series={shape.series}
            counts={shape.byAgent}
            colours={palette}
            selected={only}
            onSelect={(key) => setOnly(key)}
          />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h2">Over time, by agent</Typography>
          <Typography variant="body2" color="text.secondary">
            Not enough history to draw a shape yet. One write, or writes all
            inside one bucket, is a number rather than a trend — and drawing a
            single bar labelled &ldquo;over time&rdquo; would be inventing one.
          </Typography>
        </Paper>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h2">Per agent</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          What each one did, when it is meant to run, and whether it is keeping
          to it.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fit, minmax(340px, 1fr))",
            },
            alignItems: "stretch",
          }}
        >
          {shown.map((agent) => {
            const index = shape.series.indexOf(agent.name);
            const mine = writes.find((w) => w.agent.name === agent.name);
            return (
              <AgentCard
                key={`${agent.app_type}:${agent.name}`}
                agent={agent}
                rows={mine?.page?.items || []}
                colour={palette[(index < 0 ? 0 : index) % palette.length]}
                onOpen={() => setOpenName(agent.name)}
              />
            );
          })}
        </Box>
      </Box>

      <AgentControls
        agent={open}
        onClose={() => setOpenName(null)}
        onSchedule={
          post && open
            ? async (body) => {
                await post(`/agents/${open.app_type}/schedule`,
                           { agent: open.name, ...body });
                await load();
              }
            : null
        }
        onSuspend={
          post && open
            ? async (body) => {
                await post(`/agents/${open.app_type}/suspend`,
                           { agent: open.name, ...body });
                await load();
              }
            : null
        }
        onConfigure={
          post && open
            ? async (body) => {
                await post(`/agents/${open.app_type}/config`,
                           { agent: open.name, ...body });
                await load();
              }
            : null
        }
        onBudget={
          post && open
            ? async (body) => {
                await post(`/agents/${open.app_type}/budget`,
                           { agent: open.name, ...body });
                await load();
              }
            : null
        }
      />
    </Stack>
  );
}

/**
 * One agent: what it did, and what it is supposed to do.
 *
 * The two halves are deliberately adjacent. A gap in the history above is
 * only legible against the cadence that was expected, and somebody who sees
 * one wants to change it without going to a settings page to do it.
 */
function AgentCard({ agent, rows, colour, onOpen }) {
  const modes = { unattended: 0, interactive: 0, unrecorded: 0 };
  const verbs = {};
  for (const row of rows) {
    const mode = row.provenance?.mode;
    if (mode === "unattended") modes.unattended += 1;
    else if (mode === "interactive") modes.interactive += 1;
    else modes.unrecorded += 1;
    const verb = row.provenance?.action;
    if (verb) verbs[verb] = (verbs[verb] || 0) + 1;
  }
  const actions = Object.entries(verbs).sort((a, b) => b[1] - a[1]);
  const late = agent.lateness || {};

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            backgroundColor: colour,
            color: "background.paper",
            fontSize: "0.6875rem",
            fontWeight: 600,
          }}
        >
          {initials(agent.label || agent.name)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
            {agent.label || agent.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "ui-monospace, monospace" }}>
            {agent.name} · {agent.app_type}
          </Typography>
        </Box>
        <Lateness state={late} />
      </Stack>

      {/* Visible without opening anything. An agent that can reach a person
          outside this kernel is a different kind of thing from one that can
          only change a record, and a reader should not have to open a drawer
          to find out which they are looking at. Silent for the ordinary case
          — almost every agent cannot, and a badge on all of them would make
          the one that can invisible. */}
      {(agent.reach?.attended?.length || agent.reach?.unattended?.length) ? (
        <Tooltip
          title={
            (agent.reach.unattended || []).length
              ? "It may do this with nobody signed in, so a schedule or an alarm can reach somebody with no person in the loop."
              : "Only for a signed-in person."
          }
        >
          <Typography
            variant="caption"
            sx={{ color: "kernel.unattended", cursor: "help", fontWeight: 600 }}
          >
            Can contact people outside this kernel
          </Typography>
        </Tooltip>
      ) : null}

      {/* Declared against actual. The one shape this product can draw and a
          model provider cannot: it holds the statement of intent as well as
          the runs, and the disagreement between them is the interesting part. */}
      <Disclose label="When it was allowed to run, and when it did">
        <Kept agent={agent} />
      </Disclose>

      {/* Past the warning and still running. Not a lateness state, because it
          stops nothing — so it sits under the row rather than replacing the
          badge, which would make a working agent read as a stopped one. */}
      {agent.standing?.state === "warning" ? (
        <Tooltip
          title={
            `Spent ${dollars(agent.standing.spent)} against a limit of `
            + `${dollars(agent.budget?.limit_usd || 0)} `
            + `${agent.budget?.period_label || ""}. It keeps running until the limit.`
          }
        >
          <Typography
            variant="caption"
            sx={{ color: "kernel.attention", cursor: "help" }}
          >
            {dollars(agent.standing.spent)} of{" "}
            {dollars(agent.budget?.limit_usd || 0)} spent{" "}
            {agent.budget?.period_label || ""}
          </Typography>
        </Tooltip>
      ) : null}

      <Figures sx={{ columnGap: 3 }}>
        <Figure
          value={count(rows.length)}
          label="writes"
          basis="Rows in the audit log naming this agent, from its own read."
        />
        <Figure
          value={count(agent.spend?.runs || 0)}
          label="runs"
          basis="Runs recorded through the kernel. Anything driven from a terminal before the kernel was in the path left an audit row and none here."
        />
        <Figure
          value={dollars(agent.spend?.usd || 0)}
          label="cost · at list"
          basis={agent.spend?.basis}
        />
      </Figures>

      {rows.length ? (
        <Box>
          <Proportion
            segments={[
              { value: modes.unattended, colour: "kernel.unattended",
                label: "no person behind it",
                detail: "The kernel stamped mode: unattended — the token carried no username claim." },
              { value: modes.interactive, colour: "primary.main",
                label: "a person was there",
                detail: "The kernel stamped mode: interactive." },
              { value: modes.unrecorded, colour: "kernel.stale",
                label: "no mode recorded",
                detail: "Written before LO-55. Counted on its own rather than assumed to be either." },
            ]}
            total={rows.length}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
            {modes.unattended
              ? `${modes.unattended} with nobody there`
              : "every one had a person behind it"}
            {rows[0]?.at ? ` · last ${relative(rows[0].at)}` : ""}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No write in the log names it. Declared, bounded, and it has not
          acted — which is a real state and the one every agent is in before
          its first run.
        </Typography>
      )}

      {actions.length ? (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
          {actions.map(([verb, n]) => (
            <Chip
              key={verb}
              size="small"
              variant="outlined"
              label={`${verb} · ${n}`}
              sx={{ fontFamily: "ui-monospace, monospace" }}
            />
          ))}
        </Stack>
      ) : null}

      {/* A summary and a way in. Nine controls in a card in a three-column
          grid was the wrong space for them; this says what is in force and
          the drawer is where it changes. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: "auto",
          pt: 1.25,
          borderTop: 1,
          borderColor: "divider",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {summary(agent)}
        </Typography>
        <Button size="small" onClick={onOpen} sx={{ minWidth: 0, px: 1 }}>
          Settings
        </Button>
      </Stack>
    </Paper>
  );
}

/** What is in force, in one line, for the foot of a card. */
function summary(agent) {
  if (agent.suspension) {
    return `Switched off${
      agent.suspension.reason ? ` — ${agent.suspension.reason}` : ""
    }`;
  }
  const s = agent.schedule;
  if (!s?.enabled) return "Runs when somebody runs it";
  const how = CADENCES[s.every_minutes] || `every ${s.every_minutes} min`;
  if (!s.window_start) return how;
  return `${how}, ${readClock(s.window_start)}–${readClock(s.window_end)} ${s.timezone}`;
}

const CADENCES = {
  60: "Every hour",
  180: "Every 3 hours",
  360: "Every 6 hours",
  720: "Every 12 hours",
  1440: "Every day",
  10080: "Every week",
};

/**
 * On time, late, waiting, never run, on demand.
 *
 * Five states and `waiting` is the one this screen exists to make visible: an
 * agent with an overnight window read at noon is a full cadence overdue by
 * arithmetic and behaving exactly as declared. The word carries it, not the
 * colour — a state rendered as an amber dot is a state a colour-blind
 * operator cannot read.
 */
function Lateness({ state }) {
  const tone = {
    off: "kernel.attention",
    over_budget: "kernel.refused",
    late: "kernel.refused",
    waiting: "kernel.stale",
    never_run: "kernel.attention",
    on_time: "kernel.permitted",
    on_demand: "text.secondary",
  }[state.state] || "text.secondary";

  const word = {
    off: "off",
    over_budget: "over budget",
    late: "late",
    waiting: "waiting",
    never_run: "never run",
    on_time: "on time",
    on_demand: "on demand",
  }[state.state] || "unknown";

  return (
    <Tooltip title={state.detail || ""}>
      <Typography variant="overline" sx={{ color: tone, cursor: "help", flexShrink: 0 }}>
        {word}
      </Typography>
    </Tooltip>
  );
}

/** When this was read, and whether it is still reading. */
function Freshness({ readAt, now, live, loading, onToggle, onRefresh }) {
  const seconds = readAt ? Math.max(0, Math.round((now - readAt) / 1000)) : null;
  const ago =
    seconds == null
      ? "not read yet"
      : seconds < 5
        ? "read just now"
        : seconds < 60
          ? `read ${seconds}s ago`
          : `read ${Math.round(seconds / 60)}m ago`;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Tooltip
        title={
          live
            ? "Re-reads every 30 seconds while this tab is visible. One request per agent each time — nothing is pushed, so a write from a second ago appears at the next read."
            : "Not re-reading. Each pass costs one read per agent; turn it back on when you want the page to keep up."
        }
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", cursor: "help" }}>
          <Box
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: loading
                ? "primary.main"
                : live
                  ? "kernel.permitted"
                  : "kernel.stale",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {loading ? "reading…" : ago}
            {live ? "" : " · not live"}
          </Typography>
        </Stack>
      </Tooltip>
      <Button size="small" onClick={onRefresh} disabled={loading} sx={{ minWidth: 0, px: 1 }}>
        Refresh
      </Button>
      <Button size="small" onClick={onToggle} sx={{ minWidth: 0, px: 1 }}>
        {live ? "Pause" : "Go live"}
      </Button>
    </Stack>
  );
}
